/** Environmental Wet uses campaign time. Its own start/end pair retains the drying
 * duration while expiry is held by water contact. An existing spell Wet occupies
 * the target's ordinary Wet slot; contact leaves that row unchanged. */
import { encounterWaterModel, type WaterModel } from './traversal-3d.js';
import { moverHasMovementFlag, type DifficultTerrainReaders } from './difficult-terrain.js';
import { ftCampaignNowSeconds, ftCampaignTimeAddSeconds, type CampaignCalendarEmbed, type CampaignClockCols } from '../../shared/campaign-clock.js';
import { itemsCol, layerListEntryUuid, layerValue } from '../../shared/array-types.js';

type Row = Record<string, unknown>;
export const WATER_WET_ID = 'E3169368-A4EF-476F-B699-5EFE62C7CE2A';
const units = ['year', 'month', 'day', 'hour', 'minute', 'second'] as const;
export interface WaterWetChange { before: Row | null; after: Row }
const inlineReaders: DifficultTerrainReaders = {
  value: (row, name) => (row.item_layer_types as Row | undefined)?.[name] ?? row[name],
  entries: (row, name) => { const value = (row.item_layer_types as Row | undefined)?.[name] ?? row[name]; return Array.isArray(value) ? value : value == null ? [] : [value]; },
  definition: () => null,
  statuses: row => (row.statuses as { target?: Row[] } | undefined)?.target ?? [],
};
export function isWaterWet(row: Row): boolean {
  return wetItemId(row) === WATER_WET_ID
    && String(row.status_origin_id).toUpperCase() === WATER_WET_ID
    && !!row.status_target_id && String(row.status_target_id).toUpperCase() === String(row.status_caster_id ?? row.status_target_id).toUpperCase();
}
function wetItemId(row: Row): string {
  return String(layerValue(row.status_item_id) ?? itemsCol(row, 'item_id') ?? '').toUpperCase();
}
/** Global planes use physical overlap. Local authored bodies use the movement contract:
 * surfaceFt is the walkable floor and depthFt is its submerged depth. */
export function waterContactDepth(water: WaterModel | null, x: number, y: number, feet: number, height: number): number {
  if (!water || ![x, y, feet, height].every(Number.isFinite) || height <= 0) return 0;
  let depth = water.level === null ? 0 : Math.max(0, water.level - feet);
  for (const b of water.bodies) {
    if (x < b.x.min || x > b.x.max || y < b.y.min || y > b.y.max
      || feet > b.surfaceFt + 0.05 || feet < b.surfaceFt - b.depthFt - 0.05) continue;
    depth = Math.max(depth, b.depthFt);
  }
  return Math.max(0, Math.min(height, depth));
}
export function wetDryingSeconds(depth: number, height: number): number {
  if (!(depth > 0) || !(height > 0)) return 0;
  const fraction = Math.min(1, depth / height);
  return fraction <= 0.1 ? 6 : Math.min(600, Math.ceil((6 + 594 * (fraction - 0.1) / 0.9) / 6) * 6);
}
export function waterWetHeld(encounter: Row, row: Row, height = 1, movementReaders: DifficultTerrainReaders = inlineReaders): boolean {
  if (moverHasMovementFlag(row, movementReaders, 'walks_on_water_and_ice')) return false;
  const p = row.position as Row | undefined;
  return waterContactDepth(encounterWaterModel(encounter), Number(p?.x_pos ?? 0), Number(p?.y_pos ?? 0), Number(p?.z_pos ?? 0), height) > 0;
}
export function waterWetClock(row: Row, prefix: 'status_starts_campaign' | 'status_ends_campaign'): CampaignClockCols {
  return Object.fromEntries(units.map(u => [u, Number(row[`${prefix}_${u}`] ?? 0)])) as unknown as CampaignClockCols;
}
/** Check every segment against body footprints exactly, including narrow puddles between route vertices. */
export function routeWaterDepth(water: WaterModel | null, route: readonly (readonly number[])[], height: number): number {
  let peak = 0;
  const sample = (p: readonly number[]): void => { peak = Math.max(peak, waterContactDepth(water, p[0]!, p[1]!, p[2]!, height)); };
  for (let i = 0; i < route.length; i++) {
    const b = route[i]!; sample(b);
    if (!i || !water) continue;
    const a = route[i - 1]!;
    for (const body of water.bodies) {
      let lo = 0, hi = 1;
      for (const [axis, min, max] of [[0, body.x.min, body.x.max], [1, body.y.min, body.y.max], [2, body.surfaceFt - body.depthFt - height, body.surfaceFt]] as const) {
        const d = b[axis]! - a[axis]!;
        if (d === 0) { if (a[axis]! < min || a[axis]! > max) hi = -1; }
        else { const t0 = (min - a[axis]!) / d, t1 = (max - a[axis]!) / d; lo = Math.max(lo, Math.min(t0, t1)); hi = Math.min(hi, Math.max(t0, t1)); }
      }
      if (lo <= hi) for (const t of [lo, hi, (lo + hi) / 2]) sample(a.map((v, j) => v + (b[j]! - v) * t));
    }
  }
  return peak;
}
export function resolveWaterWet(input: {
  encounter: Row; actor: Row; actorId: string; route: readonly (readonly number[])[]; height: number;
  now: CampaignClockCols; calendar: CampaignCalendarEmbed; newId: () => string; teleport?: boolean;
  movementReaders?: DifficultTerrainReaders;
}): WaterWetChange | null {
  const read = input.movementReaders ?? inlineReaders;
  if (moverHasMovementFlag(input.actor, read, 'walks_on_water_and_ice')) return null;
  const defenses = (input.actor.computed as { creature?: { defenses?: { immune_status?: unknown[] } } } | undefined)?.creature?.defenses;
  if (read.entries(input.actor, 'immune_status_effect').some(entry => layerListEntryUuid(entry).toUpperCase() === WATER_WET_ID)
    || defenses?.immune_status?.some(entry => String((entry as Row)?.item_layer_effect ?? '').toUpperCase() === WATER_WET_ID)) return null;
  const water = encounterWaterModel(input.encounter);
  const route = input.route;
  if (!route.length) return null;
  const before = read.statuses(input.actor).find(row => !Number(layerValue(row.status_deleted) ?? 0)
    && wetItemId(row) === WATER_WET_ID) ?? null;
  if (before && !isWaterWet(before)) return null;
  const peak = routeWaterDepth(water, input.teleport ? [route[route.length - 1]!] : route, input.height);
  const start = route[0]!;
  const wasInWater = waterContactDepth(water, start[0]!, start[1]!, start[2]!, input.height) > 0;
  if (peak <= 0 && (!before || !wasInWater)) return null;
  const seconds = (cols: CampaignClockCols): number => ftCampaignNowSeconds(cols, input.calendar);
  const now = seconds(input.now);
  const previousEnd = before ? seconds(waterWetClock(before, 'status_ends_campaign')) : 0;
  const previousDuration = before ? Math.max(0, Math.min(600, previousEnd - seconds(waterWetClock(before, 'status_starts_campaign')))) : 0;
  const duration = Math.max(wetDryingSeconds(peak, input.height), wasInWater ? previousDuration : Math.max(0, previousEnd - now));
  if (!duration) return null;
  const ends = ftCampaignTimeAddSeconds(input.now, input.calendar, duration);
  const after: Row = { status_id: before?.status_id ?? input.newId(), status_item_id: WATER_WET_ID,
    status_origin_id: WATER_WET_ID, status_caster_id: input.actorId, status_target_id: input.actorId, status_stack: 1 };
  for (const unit of units) { after[`status_starts_campaign_${unit}`] = input.now[unit]; after[`status_ends_campaign_${unit}`] = ends[unit]; }
  return { before: before ? { ...before } : null, after };
}
export function projectWaterWet(row: Row, change: WaterWetChange, undo = false): void {
  const statuses = (row.statuses ??= {}) as Row;
  for (const mode of ['target', 'caster']) {
    const list = Array.isArray(statuses[mode]) ? statuses[mode] as Row[] : [];
    const held = list.find(isWaterWet);
    const next = list.filter(s => !isWaterWet(s));
    const entry = undo ? change.before : { ...held, ...change.after };
    if (entry) next.push(entry);
    statuses[mode] = next;
  }
}
